/* eslint-disable react/no-unused-prop-types */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import ResizeObserver from 'resize-observer-polyfill'
import './baseMasonry.css'

export declare interface MasonryProps {
  id?: string;
  data?: [];
  renderItem?(item: any, width: number, index: number): React.ReactNode;
  itemKey?(item: any, index: number): React.Key;
  columnCount?: number | ((width?: number) => number);
  gutter?: number;
  prefixClass?: string;
  zIndex?: (index: number, item: any) => number;
  instance?: React.Ref<{ layout: () => void }>;
}

export declare interface MasonryItemProps extends MasonryProps {
  item: any;
  deep?:any;
  index: number;
  columns: React.MutableRefObject<number[]>;
  columnWidth: number;
  onHeightChange(height: number): void;
  prefixClass?: string;
  zIndex?: (index: number, item: any) => number;
}

const DEFAULT_GUTTER = 20
const DEFAULT_COLUMN_COUNT = 5

const MasonryItem = forwardRef<{ layout:() => void }, MasonryItemProps>(
  (props: MasonryItemProps, ref) => {
    const {
      item,
      index,
      renderItem,
      columnWidth,
      columns,
      gutter = DEFAULT_GUTTER,
      onHeightChange,
      prefixClass,
      zIndex,
      deep,
    } = props
    const $item = useRef<HTMLDivElement>(null)
    const didMounted = useRef(false)

    const layout = () => {
      if (!$item.current) return
      const height = $item.current.clientHeight
      let minHeight = Math.min(...columns.current)
      if (minHeight === Infinity) {
        minHeight = 0
      }
      let minIndex = columns.current.findIndex((h) => h === minHeight)
      if (minIndex === -1) {
        minIndex = 0
      }
      if (didMounted.current) {
        $item.current.style.transition = 'all 0.2s linear'
      }

      if (
        $item.current.style.getPropertyValue('--yk-masonry-item-p')
        && columnWidth
      ) {
        didMounted.current = true
      }

      $item.current.style.setProperty(
        '--yk-masonry-item-p',
        `${(columnWidth + gutter) * minIndex}px,${minHeight}px,0`
      )
      $item.current.style.opacity = '1'

      columns.current[minIndex] = minHeight + height + gutter
      onHeightChange(Math.max(...columns.current))
    }

    useImperativeHandle(ref, () => ({
      layout,
    }))

    useEffect(() => {
      layout()
    }, [columnWidth, deep])

    return (
      <div
        className={`${prefixClass}MasonryItem`}
        ref={$item}
        style={{ width: columnWidth, zIndex: zIndex?.(index, item) }}
        // eslint-disable-next-line react/no-unknown-property
        yk-masonry-index={index}
      >
        {renderItem?.(item, columnWidth, index)}
      </div>
    )
  })

const Masonry = forwardRef<HTMLDivElement, MasonryProps>((props, ref) => {
  const {
    data = [],
    renderItem,
    itemKey,
    columnCount = DEFAULT_COLUMN_COUNT,
    gutter = DEFAULT_GUTTER,
    id,
    prefixClass,
    zIndex,
    instance,
  } = props
  const [columnWidth, setColumnWidth] = useState(0)
  const [deep, setDeep] = useState({})
  const [height, setHeight] = useState(0)
  const itemCountRef = useRef(0)
  const columnCountRef = useRef(0)

  const columns = useRef<number[]>(new Array(columnCountRef.current).fill(0))
  const $masonry = (ref as any) || useRef<HTMLDivElement>(null)
  const $items = useRef<({ layout: () => void } | null)[]>([])
  const widthRef = useRef(0)
  const ticking = useRef(false)

  const verify = (init?: boolean) => {
    const width = $masonry.current?.clientWidth
    if (!width) {
      return 0
    }

    const count = typeof columnCount === 'function' ? columnCount(width) : columnCount

    if (!count) {
      return 0
    }

    columnCountRef.current = count

    const itemWidth = Math.floor(
      ((width - gutter * (columnCountRef.current - 1)) * 100)
          / columnCountRef.current
    ) / 100

    if (itemWidth === columnWidth && !init) {
      return 0
    }

    return itemWidth
  }

  const verifyRef = useRef(verify)

  useEffect(() => {
    verifyRef.current = verify
  }, [columnCount, columnWidth])

  const layout = (itemWidth: number) => {
    ticking.current = true
    columns.current = new Array(columnCountRef.current).fill(0)
    setColumnWidth(itemWidth)
    setDeep({})
  }

  useImperativeHandle(instance, () => ({
    layout: () => {
      const width = verifyRef.current(true)
      if (width && !ticking.current) {
        layout(width)
      }
    },
  }))

  useEffect(() => {
    widthRef.current = $masonry.current?.clientWidth || 0
    const resizeObserver = new ResizeObserver((entries) => {
      if (widthRef.current === entries[0].target.clientWidth) {
        return
      }
      widthRef.current = entries[0].target.clientWidth
      const width = verifyRef.current()
      if (width && !ticking.current) {
        layout(width)
      }
    })
    resizeObserver.observe($masonry.current)
    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    const w = $masonry.current?.clientWidth || 0
    const count = typeof columnCount === 'function' ? columnCount(w) : columnCount
    if (count === columnCountRef.current) {
      return
    }
    const width = verify(true)
    if (width && !ticking.current) {
      layout(width)
    }
  }, [columnCount])

  return (
    <div
      className={`${prefixClass}Masonry`}
      ref={$masonry}
      id={id}
      style={{ height }}
    >
      {data.map((item, index) => (
        <MasonryItem
          ref={(ele) => {
            $items.current[index] = ele
          }}
          prefixClass={prefixClass}
          key={itemKey ? itemKey(item, index) : index}
          item={item}
          index={index}
          zIndex={zIndex}
          renderItem={renderItem}
          columnWidth={columnWidth}
          deep={deep}
          columnCount={columnCountRef.current}
          columns={columns}
          gutter={gutter}
          onHeightChange={(h) => {
            itemCountRef.current += 1
            setHeight(h)
            if (itemCountRef.current === data.length) {
              itemCountRef.current = 0
              ticking.current = false
            }
          }}
        />
      ))}
    </div>
  )
})

Masonry.defaultProps = {
  data: [],
  columnCount: DEFAULT_COLUMN_COUNT,
  gutter: DEFAULT_GUTTER,
  prefixClass: 'yk',
}

export default Masonry
